import { Injectable, Input } from "@angular/core";
import { FILE_MANAGER_ROUTES, FILE_METADATA_ROUTES } from "./file.routes";
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from "@angular/common/http";
import {
    catchError,
    distinctUntilChanged,
    exhaustMap,
    map,
    startWith,
    switchMap,
    takeUntil,
    takeWhile,
    timeout,
} from "rxjs/operators";
import {
    BehaviorSubject,
    forkJoin,
    Observable,
    of,
    Subject,
    throwError,
    timer,
} from "rxjs";
import { FileUploadMeta } from "app/onion/learning-object-builder/components/content-upload/app/services/typings";
import { LearningObject } from "@entity";
import { SOURCE_CODE_EXTENSIONS } from "./source.extensions";
import type { FileProcessingStatusResponse } from "./file-processing-status.types";

interface AddedFileMetadataResponse {
    id: string;
    name: string;
    fileType: string;
    extension: string;
    date: string;
    fullPath?: string;
    size: number;
    description?: string;
    packageable?: boolean;
    storageRevision?: number;
    downloadURL?: string;
}

// Matches DOCLING_SUPPORTED_EXTENSIONS in clark-service's Docling driver.
const AI_MATERIAL_EXTENSIONS = new Set([
    "docx",
    "html",
    "md",
    "pdf",
    "pptx",
    "txt",
    "xlsx",
]);

@Injectable({
    providedIn: "root",
})
export class FileService {
    private headers = new HttpHeaders();
    private fileProcessingStatuses = new BehaviorSubject<
        ReadonlyMap<string, string>
    >(new Map());
    readonly fileProcessingStatuses$ =
        this.fileProcessingStatuses.asObservable();

    @Input()
    learningObject$: Observable<LearningObject> =
        new Observable<LearningObject>();

    constructor(private http: HttpClient) {}

    /** Publishes a snapshot so later caller mutations cannot change emitted statuses. */
    setFileProcessingStatuses(statuses: ReadonlyMap<string, string>): void {
        this.fileProcessingStatuses.next(new Map(statuses));
    }

    static supportsAiExtraction(extension: string | undefined): boolean {
        return AI_MATERIAL_EXTENSIONS.has(extension?.toLowerCase() ?? "");
    }

    /** Gets the processing status for the file's current storage revision. */
    getFileProcessingStatus(
        learningObjectId: string,
        fileId: string,
    ): Observable<FileProcessingStatusResponse> {
        return this.http.get<FileProcessingStatusResponse>(
            FILE_METADATA_ROUTES.GET_FILE_PROCESSING_STATUS(
                learningObjectId,
                fileId,
            ),
            { headers: this.headers, withCredentials: true },
        );
    }

    /**
     * Polls supported materials until one is processed. Changing files or revisions
     * cancels old checks and resets readiness; unsubscribing stops all polling.
     */
    watchAiMaterialReadiness(
        learningObjects: Observable<LearningObject | undefined>,
    ): Observable<boolean> {
        return learningObjects.pipe(
            map((learningObject) => ({
                learningObjectId: learningObject?.id,
                files: (learningObject?.materials?.files ?? [])
                    .filter(
                        (file) =>
                            file._id &&
                            FileService.supportsAiExtraction(file.extension),
                    )
                    .map((file) => ({
                        id: file._id,
                        revision: file.storageRevision,
                        date: file.date,
                        extension: file.extension.toLowerCase(),
                    }))
                    .sort((left, right) => left.id.localeCompare(right.id)),
            })),
            distinctUntilChanged(
                (previous, current) =>
                    JSON.stringify(previous) === JSON.stringify(current),
            ),
            switchMap(({ learningObjectId, files }) => {
                if (!learningObjectId || !files.length) return of(false);
                const unavailableFiles = new Set<string>();
                return timer(0, 5000).pipe(
                    exhaustMap(() =>
                        forkJoin(
                            files.map((file) => {
                                if (unavailableFiles.has(file.id))
                                    return of(null);
                                return this.getFileProcessingStatus(
                                    learningObjectId,
                                    file.id,
                                ).pipe(
                                    timeout(15000),
                                    catchError((error: unknown) => {
                                        if (
                                            error instanceof
                                                HttpErrorResponse &&
                                            error.status >= 400 &&
                                            error.status < 500 &&
                                            error.status !== 408 &&
                                            error.status !== 429
                                        ) {
                                            unavailableFiles.add(file.id);
                                        }
                                        return of(null);
                                    }),
                                );
                            }),
                        ),
                    ),
                    map((statuses) =>
                        statuses.some(
                            (status, index) =>
                                status?.fileId === files[index].id &&
                                (files[index].revision === undefined ||
                                    status.storageRevision ===
                                        files[index].revision) &&
                                (status.processingStatus === "success" ||
                                    status.processingStatus ===
                                        "partial_success"),
                        ),
                    ),
                    takeWhile(
                        (ready) =>
                            !ready && unavailableFiles.size < files.length,
                        true,
                    ),
                    startWith(false),
                );
            }),
            distinctUntilChanged(),
        );
    }

    /**
     * Helper: extracts the file extension from a filename (including the dot)
     * @param filename - The filename to extract extension from
     * @returns The file extension in lowercase, or empty string if no extension
     */
    private static getFileExtension(filename: string): string {
        const lastDot = filename.lastIndexOf(".");
        if (lastDot !== -1 && lastDot < filename.length - 1) {
            return filename.substring(lastDot).toLowerCase();
        }
        return "";
    }

    /**
     * Helper: returns the language identifier for syntax highlighting based on file extension
     * @param filename - The filename to get language for
     * @returns The language identifier or empty string if not found
     */
    private static getLanguageFromExtension(filename: string): string {
        const extension = this.getFileExtension(filename);
        return SOURCE_CODE_EXTENSIONS[extension] || "";
    }

    /**
     * Helper: returns a category for the file based on its extension.
     * @param filename - The filename to categorize
     * @returns The category ('office', 'pdf', 'code', etc.) or an empty string.
     */
    private static getFileType(filename: string): {
        type: string;
        ext: string;
        language?: string;
    } {
        const extension = this.getFileExtension(filename);

        const typeExtensions: { [type: string]: string[] } = {
            notebook: [".ipynb"],
            office: [
                ".docx",
                ".doc",
                ".pptx",
                ".ppt",
                ".ppsx",
                ".pps",
                ".potx",
                ".pot",
                ".xlsx",
                ".xls",
            ],
            pdf: [".pdf"],
            image: [".png", ".jpg", ".jpeg", ".webp"],
        };

        // Check for source code files first
        const language = this.getLanguageFromExtension(filename);
        if (language) {
            return { type: "code", ext: extension, language };
        }

        // Iterate over [type, exts] entries to do a reverse lookup and obtain
        // the matching type for the given file extension
        const entry = Object.entries(typeExtensions).find(([_, exts]) =>
            exts.includes(extension),
        );
        const type = entry ? entry[0] : "";

        return { type, ext: extension };
    }

    /**
     * Defines how to preview different file types.
     * Maps a file type identifier to a function that opens the preview.
     */
    private static readonly PREVIEW_ACTIONS: {
        [key: string]: (url: string, fileName?: string) => void;
    } = {
        // Use Microsoft's viewer
        office: (url: string) => {
            const officeViewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${url}`;
            window.open(officeViewerUrl, "_blank", "noopener,noreferrer");
        },
        // Open a new tab
        pdf: (url: string) => {
            window.open(url, "_blank", "noopener,noreferrer");
        },
        // Open code preview in new tab
        code: (url: string, fileName: string) => {
            const fileInfo = FileService.getFileType(fileName);
            const language = fileInfo.language || "text";
            const encodedUrl = encodeURIComponent(url);
            const encodedFileName = encodeURIComponent(fileName);
            const previewUrl = `/preview/code?url=${encodedUrl}&language=${language}&filename=${encodedFileName}`;
            window.open(previewUrl, "_blank", "noopener,noreferrer");
        },
        // Render Jupyter notebooks as static HTML in the shared preview shell
        notebook: (url: string, fileName: string) => {
            const encodedUrl = encodeURIComponent(url);
            const encodedFileName = encodeURIComponent(fileName);
            const previewUrl = `/preview/code?url=${encodedUrl}&type=notebook&filename=${encodedFileName}`;
            window.open(previewUrl, "_blank", "noopener,noreferrer");
        },
    };

    /**
     * Helper: returns true if filename can be previewed
     */
    static canPreview(filename: string): boolean {
        const type = this.getFileType(filename).type;
        return type === "image" || type in this.PREVIEW_ACTIONS;
    }

    /**
     * Download the learning object file (for any type).
     * @param url public/authorized file URL
     * @param name file name to use for download
     */
    async downloadLearningObjectFile(
        url: string,
        name: string,
    ): Promise<string> {
        return this.http
            .get(url, {
                withCredentials: true,
                responseType: "blob",
                observe: "response",
            })
            .pipe(catchError(this.handleError))
            .toPromise()
            .then((response: any) => {
                // Extract the blob from the response
                const blob = response.body;
                // Create a URL for the blob
                const blobUrl = window.URL.createObjectURL(blob);
                // Trigger a download using an anchor element
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = name;
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                // Revoke the blob URL to free memory
                window.URL.revokeObjectURL(blobUrl);
                return "";
            });
    }

    /**
     * Gets raw text content for a learning object file.
     * @param url public/authorized file URL
     */
    async getLearningObjectFileContent(url: string): Promise<string> {
        return this.http
            .get(url, {
                withCredentials: true,
                responseType: "text",
            })
            .pipe(catchError(this.handleError))
            .toPromise();
    }

    /**
     * Preview the file if it can be previewed.
     * @param url file URL
     * @param fileName file name
     */
    async previewLearningObjectFile(
        url: string,
        fileName: string,
    ): Promise<void> {
        const fileType = FileService.getFileType(fileName).type;

        if (fileType === "image") {
            return this.previewImage(url, fileName);
        }

        const previewAction = FileService.PREVIEW_ACTIONS[fileType];

        if (previewAction) {
            previewAction(url, fileName);
        }
    }

    private previewImage(url: string, fileName: string): Promise<void> {
        // Open the tab before the request so the browser treats it as a user action.
        const previewWindow = window.open("about:blank", "_blank");
        if (!previewWindow) {
            return Promise.resolve();
        }
        previewWindow.opener = null;
        previewWindow.document.title = fileName;

        return new Promise<void>((resolve, reject) => {
            this.http
                .get(url, { withCredentials: true, responseType: "blob" })
                .pipe(catchError(this.handleError))
                .subscribe({
                    next: (blob) => {
                        const mimeTypes: Record<string, string> = {
                            ".png": "image/png",
                            ".jpg": "image/jpeg",
                            ".jpeg": "image/jpeg",
                            ".webp": "image/webp",
                        };
                        const mimeType =
                            mimeTypes[FileService.getFileExtension(fileName)];
                        const imageBlob =
                            blob.type === mimeType
                                ? blob
                                : blob.slice(0, blob.size, mimeType);
                        const blobUrl = URL.createObjectURL(imageBlob);
                        if (previewWindow.closed) {
                            URL.revokeObjectURL(blobUrl);
                            resolve();
                            return;
                        }

                        // Navigating to the image puts its blob URL in the address bar.
                        previewWindow.location.replace(blobUrl);

                        resolve();
                    },
                    error: (error) => {
                        if (!previewWindow.closed) {
                            previewWindow.document.body.textContent =
                                "Unable to preview image.";
                        }
                        reject(error);
                    },
                });
        });
    }

    async deleteLearningObjectFileMetadata(
        learningObjectId: string,
        fileId: string,
    ): Promise<void> {
        this.http
            .delete(
                FILE_METADATA_ROUTES.DELETE_LEARNING_OBJECT_FILE_METADATA(
                    learningObjectId,
                    fileId,
                ),
                {
                    headers: this.headers,
                    withCredentials: true,
                },
            )
            .pipe(catchError(this.handleError))
            .toPromise();
    }

    updateFileDescription(
        objectId: string,
        fileId: string,
        description: string,
    ): Promise<any> {
        const route = FILE_METADATA_ROUTES.UPDATE_LEARNING_OBJECT_FILE_METADATA(
            objectId,
            fileId,
        );
        return this.http
            .patch(
                route,
                { description },
                {
                    headers: this.headers,
                    withCredentials: true,
                    responseType: "text",
                },
            )
            .pipe(catchError(this.handleError))
            .toPromise();
    }

    // Updates the README of a learning object
    async updateReadme(id: string): Promise<any> {
        return await this.http
            .patch(
                FILE_MANAGER_ROUTES.UPDATE_README(id),
                {},
                {
                    headers: this.headers,
                    withCredentials: true,
                    responseType: "text",
                },
            )
            .pipe(catchError(this.handleError))
            .toPromise();
    }

    /**
     * Adds file meta to a Learning Object's materials
     * Adding files are handled by a job queue to avoid sending too large of a payload to the server
     *
     * @param {string} authorUsername [The Learning Object's author's username]
     * @param {string} objectId [The Id of the Learning Object]
     * @param {FileUploadMeta[]} files [List of file meta to be added]
     * @returns {Promise<LearningObject.Material.File[]>}
     * @memberof LearningObjectService
     */
    addFileMeta({
        objectId,
        files,
    }: {
        objectId: string;
        files: FileUploadMeta[];
    }): Promise<LearningObject.Material.File[]> {
        const route =
            FILE_METADATA_ROUTES.ADD_LEARNING_OBJECT_FILE_METADATA(objectId);
        const MAX_PER_REQUEST = 100;
        const responses$: Promise<LearningObject.Material.File[]>[] = [];
        const completed$: Subject<boolean> = new Subject<boolean>();
        const sendNextBatch$: Subject<void> = new Subject<void>();

        const response = new Promise<LearningObject.Material.File[]>(
            (resolve, reject) => {
                sendNextBatch$.pipe(takeUntil(completed$)).subscribe(() => {
                    const batch = files.splice(0, MAX_PER_REQUEST);
                    if (batch.length) {
                        this.handleFileMetaBatch(
                            route,
                            batch,
                            responses$,
                            sendNextBatch$,
                        );
                    } else {
                        this.handleFileMetaRequestQueueCompletion(
                            completed$,
                            responses$,
                        )
                            .then(resolve)
                            .catch(reject);
                    }
                });
            },
        );
        sendNextBatch$.next();
        return response;
    }

    /**
     * Calls LO service to update the packageable status of toggled files
     *
     * @param learningObjectID The current learning object's ID
     * @param fileIDs An array of file IDs that need to be updated
     * @param state The new packageable property to update to
     * @returns A promise
     */
    // TODO: Move to bundling service
    toggleBundle(learningObjectId: string, fileIDs: string[], state: boolean) {
        return this.http
            .patch(
                FILE_MANAGER_ROUTES.UPDATE_BUNDLE(learningObjectId),
                {
                    fileIDs: fileIDs,
                    packagable: state,
                },
                { headers: this.headers, withCredentials: true },
            )
            .pipe(catchError(this.handleError))
            .toPromise();
    }

    /**
     * Handles making request to upload batch of file meta
     *
     * @private
     * @param {string} route [Route to make request to]
     * @param {FileUploadMeta[]} batch [Batch of file meta to be added]
     * @param {Promise<LearningObject.Material.File[]>[]} responses$ [List of response promises to append to]
     * @param {Subject<void>} sendNextBatch$ [Observable used to signal that the next batch should be sent]
     * @memberof LearningObjectService
     */
    private handleFileMetaBatch(
        route: string,
        batch: FileUploadMeta[],
        responses$: Promise<LearningObject.Material.File[]>[],
        sendNextBatch$: Subject<void>,
    ) {
        const response$: Promise<LearningObject.Material.File[]> = this.http
            .post<AddedFileMetadataResponse[]>(
                route,
                { fileMeta: batch },
                {
                    headers: this.headers,
                    withCredentials: true,
                },
            )
            .pipe(catchError(this.handleError))
            .toPromise()
            .then((files) =>
                files.map(({ id, ...file }) => ({
                    ...file,
                    _id: id,
                })),
            );
        responses$.push(response$);
        sendNextBatch$.next();
    }

    /**
     * Handles completion of requests for all file metadata that was enqueued
     *
     * @private
     * @param {Subject<boolean>} completed$ [Observable used to signal that all batches have been completed]
     * @param {Promise<LearningObject.Material.File[]>[]} responses$ [List of response promises to resolve]
     * @returns
     * @memberof LearningObjectService
     */
    private async handleFileMetaRequestQueueCompletion(
        completed$: Subject<boolean>,
        responses$: Promise<LearningObject.Material.File[]>[],
    ): Promise<LearningObject.Material.File[]> {
        completed$.next(true);
        completed$.unsubscribe();
        const fileArrays = await Promise.all(responses$);
        return flattenDeep(fileArrays) as LearningObject.Material.File[];
    }

    /**
     * Handles downloading a file by opening the stream url in a new window
     *
     * @param {LearningObject.Material.File} file [The file to be downloaded]
     * @memberof UploadComponent
     */
    handleFileDownload(
        file: LearningObject.Material.File,
        learningObject: LearningObject,
    ) {
        const loId = learningObject.id;
        return this.http
            .get(FILE_MANAGER_ROUTES.DOWNLOAD_FILE(loId, file._id), {
                headers: this.headers,
                withCredentials: true,
                responseType: "blob",
                observe: "response",
            })
            .pipe(catchError(this.handleError))
            .toPromise()
            .then((response: any) => {
                // Extract the blob from the response
                const blob = response.body;
                // Create a URL for the blob
                const blobUrl = window.URL.createObjectURL(blob);
                // Trigger a download using an anchor element
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = file.name; //Use the extracted file name
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                // Revoke the blob URL to free memory
                window.URL.revokeObjectURL(blobUrl);
                return "";
            });
    }

    private handleError(error: HttpErrorResponse) {
        if (
            error.error instanceof ErrorEvent ||
            (error.error && error.error.message)
        ) {
            // Client-side or network returned error
            return throwError(error.error.message);
        } else {
            // API returned error
            return throwError(error);
        }
    }
}

/**
 * Flattens nested arrays
 *
 * Taken from: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
 *
 * @param {any[]} arr1 [Array with nested arrays to be flattened]
 * @returns
 */
function flattenDeep(arr1: any[]): any[] {
    return arr1.reduce(
        (acc, val) =>
            Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val),
        [],
    );
}
